import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../queryKeys'
import { auditAiRequest } from '../../util/api/audit-ai-api'
import useStore from '../../util/store/auth'
import checkToken from '../../util/auth/checkToken'
import useDashboardListFilters from '../../util/useDashboardListFilters'
import { getDashboardQueryContext, getSortedMeasureIdsForQueryKey } from './dashboardHookUtils'
import { getDashboardTimezone } from '../../util/dateUtils'

/**
 * How many records under the current filters carry no date of service.
 *
 * Deliberately unaware of the date range: those records fall outside every
 * date-of-service range, so a count that moved with the range would not answer
 * how many the view is leaving out.
 */
export const useDashboardNoDateOfServiceCount = () => {
  const { directory } = useStore()
  const { filters } = useDashboardListFilters()
  const timezone = getDashboardTimezone()
  const {
    scopeFilters: { clinicianEmail, patientMrn, measureIds, location, program },
    validatedSeverity,
    resolvedDateRange: { fromDate, toDate, hasDateRange },
  } = getDashboardQueryContext(filters, timezone)
  const sortedMeasureIdsForQueryKey = getSortedMeasureIdsForQueryKey(measureIds)

  const queryKey = queryKeys.dashboardNoDateOfServiceCount(
    directory?._id ?? '',
    [
      validatedSeverity,
      clinicianEmail,
      patientMrn,
      location,
      program,
      ...(sortedMeasureIdsForQueryKey ?? []),
    ].map((value) => String(value ?? ''))
  )

  const countQuery = useQuery({
    queryKey,
    queryFn: async () => {
      await checkToken()
      return auditAiRequest.dashboard.noDateOfServiceCount({
        token: '',
        directoryId: directory?._id ?? '',
        severity: validatedSeverity,
        clinicianEmail: clinicianEmail ?? undefined,
        patientMrn: patientMrn ?? undefined,
        location,
        program,
        measureIds,
      })
    },
    enabled:
      !!directory?._id &&
      (filters.dateMode === 'dateOfService' || filters.noDateOfService === true),
  })

  const shownQuery = useQuery({
    queryKey: queryKeys.dashboardTableDocuments(directory?._id ?? '', {
      page: 1,
      limit: 1,
      fromDate,
      toDate,
      timezone,
      noVisitDate: true,
      severity: validatedSeverity,
      clinicianEmail: clinicianEmail ?? undefined,
      patientMrn: patientMrn ?? undefined,
      location,
      program,
      measureIds: sortedMeasureIdsForQueryKey,
    }),
    queryFn: async () => {
      await checkToken()
      return auditAiRequest.dashboard.tableDocuments({
        token: '',
        directoryId: directory?._id ?? '',
        page: 1,
        limit: 1,
        fromDate: fromDate ?? '',
        toDate: toDate ?? '',
        timezone,
        noVisitDate: true,
        severity: validatedSeverity,
        clinicianEmail: clinicianEmail ?? undefined,
        patientMrn: patientMrn ?? undefined,
        location,
        program,
        measureIds,
      })
    },
    enabled: !!directory?._id && filters.noDateOfService === true && hasDateRange,
  })

  return {
    ...countQuery,
    shown: shownQuery.data?.pagination.total,
    isShownLoading: shownQuery.isLoading,
  }
}
